import { Checkbox } from '../../../components/Checkbox'
import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { graphql, useFragment } from '../../../gql'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { useOrderDetailContext } from '../OrderDetailContext'
import { notifyPickingActivity } from '../lib/hooks/pickingActivityEvents'

const updateClearanceItemMutation = graphql(`
  mutation UpdateClearanceItemPicking($id: String!, $order: Int!, $picked: Int, $packed: Int) {
    updateClearanceItem(id: $id, order: $order, picked: $picked, packed: $packed) {
      data {
        picked
        packed
        amount
      }
      errors
    }
  }
`)

const PickingCheckboxCell_OrderFragment = graphql(`
  fragment PickingCheckboxCell_OrderFragment on Order {
    id
    clearanceItems {
      id
      picked
      packed
      amount
    }
    processStatus {
      id
    }
  }
`)

export function PickingCheckboxCell({ clearanceItemId }: { clearanceItemId: string }) {
  const queryClient = useQueryClient()

  const context = useOrderDetailContext()
  const order = useFragment(PickingCheckboxCell_OrderFragment, context.order)

  const clearanceItem = useMemo(
    () => order?.clearanceItems?.find(el => el.id === clearanceItemId),
    [order, clearanceItemId],
  )

  const mutatingField: 'picked' | 'packed' =
    order.processStatus.id === 'picking' ? 'picked' : order.processStatus.id === 'packing' ? 'packed' : null

  const { mutate, isPending: isLoading } = useGQLMutation(
    updateClearanceItemMutation,
    ['updateClearanceItem', clearanceItemId],
    {
      onSuccess: data => {
        queryClient.setQueryData(['order', { id: order.id }], (old: any) => {
          const updatedClearanceItems = old.order.data.clearanceItems.map((oldCi: any) =>
            oldCi.id === clearanceItemId ? { ...oldCi, ...(data ?? {}) } : oldCi,
          )
          const items = old.order.data.items
          const updatedItems =
            items?.map((item: any) => {
              if (!item.clearanceItems?.length) return item
              const allPicked = item.clearanceItems.every((ci: any) => {
                const c = updatedClearanceItems.find((uc: any) => uc.id === ci.id)
                return c && (c.picked ?? 0) >= (c.amount ?? 0)
              })
              const allPacked = item.clearanceItems.every((ci: any) => {
                const c = updatedClearanceItems.find((uc: any) => uc.id === ci.id)
                return c && (c.packed ?? 0) >= (c.amount ?? 0)
              })
              return {
                ...item,
                picked: allPicked,
                packed: allPacked,
              }
            }) ?? items
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                clearanceItems: updatedClearanceItems,
                items: updatedItems,
              },
            },
          }
        })
        notifyPickingActivity({
          orderId: Number(order.id),
          phase: order.processStatus.id === 'packing' ? 'packing' : 'picking',
          source: 'checkbox',
        })
      },
    },
  )

  const handleToggle = () => {
    const ci = order.clearanceItems.find(el => el.id === clearanceItemId)
    mutate({
      id: clearanceItemId,
      order: order.id,
      picked: mutatingField === 'picked' ? (ci.picked === ci.amount ? 0 : ci.amount) : null,
      packed: mutatingField === 'packed' ? (ci.packed === ci.amount ? 0 : ci.amount) : null,
    })
  }

  if (!(order.processStatus.id === 'picking' || order.processStatus.id === 'packing')) return null

  if (isLoading)
    return (
      <div className={'flex items-center justify-center flex-1 h-8'}>
        <div className="loader-small ease-linear" />
      </div>
    )

  return (
    <div className={'flex items-center justify-center flex-1 h-8'}>
      <Checkbox checked={clearanceItem[mutatingField] === clearanceItem.amount} onChange={handleToggle} />
    </div>
  )
}
